import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { AuthService } from '@app/services/auth.service';
import { Interval, PurchaseProduct } from '@app/services/payment.service';
import { Currency } from '@app/shared/currency';
import { CurrencyPipe } from '@app/shared/pipes/currency.pipe';
import { environment } from '@env';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonDirective } from 'primeng/button';

export type PurchaseProductFE = PurchaseProduct & {
  id: string;
  name: string;
  description: string;
  marketingFeatures: { name: string }[];
  interval: Interval;
  pricePerMonth?: number;
  price?: number;
  currency?: Currency;
  paymentLink?: string;
  contactUrl?: string;
};

@Component({
  selector: 'app-product-box',
  templateUrl: './product-box.component.html',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CurrencyPipe, ButtonDirective],
  host: {
    class: 'flex md:max-w-80 min-w-56 md:min-h-[450px] min-h-[300px]',
  },
})
export class ProductBoxComponent {
  loading = input<boolean>(false);
  product = input<PurchaseProduct | undefined>(undefined);
  selectedInterval = input<Interval>('month');

  intervalOptions = signal<Interval[]>([]);
  displayProduct = signal<PurchaseProductFE | undefined>(undefined);

  authService = inject(AuthService);

  onProductChange = effect(() => {
    const availableIntervals = this.product()?.productPrices?.map((price) => price.interval);
    this.intervalOptions.set(availableIntervals || []);

    const displayProduct = this._mapProductToFE(this.product(), this.selectedInterval());
    if (displayProduct) {
      this.displayProduct.set(displayProduct as PurchaseProductFE);
    } else {
      this.displayProduct.set(undefined);
    }
  });

  private _mapProductToFE(product: PurchaseProduct | undefined, interval: Interval): PurchaseProductFE | undefined {
    if (!product) {
      return undefined;
    }
    const priceObject = this.product()?.productPrices?.find((price) => price.interval === interval);
    const priceInCents = priceObject?.unitAmountDecimal ? Number(priceObject?.unitAmountDecimal) : undefined;
    const pricePerMonth = priceInCents && priceObject?.interval === 'year' ? priceInCents / 12 : priceInCents;
    const paymentLink = priceObject?.paymentLink
      ? `${priceObject?.paymentLink}?client_reference_id=${this.authService.currentUser()?.company.id || ''}`
      : undefined;

    return {
      ...product,
      contactUrl: environment.qira_contact_url,
      marketingFeatures: product?.marketing_features,
      interval: this.selectedInterval(),
      price: priceInCents,
      pricePerMonth: pricePerMonth,
      currency: priceObject?.currency,
      paymentLink: paymentLink,
      id: priceObject?.id || '',
    };
  }
}
